import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { useSelectionReset } from './selection-reset-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetArbitriQuery,
  useGetCampionatiQuery,
} from './api-slice'
import {
  setSelectedStagione,
  setSelectedCampionato,
  setSelectedCategoria,
  setSelectedGirone,
  setSelectedTeam,
  setSelectedNumeroGara,
  setSelectedProvvedimento,
  setSelectedArbitraggio,
  setSelectedPeriodo,
  setSelectedRitardo,
  setSelectedTessera,
  setSelectedArbitro,
  setSelectedSocieta,
} from '../container/selection'

export const useSelection = (yArbitro, ySocieta) => {
  const { data: campionati } = useGetCampionatiQuery()
  const availableStagioni = useSelector(
    state => state.selection.availableStagioni,
  )
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const availableCampionati = useSelector(
    state => state.selection.availableCampionati,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const availableGironi = useSelector(state => state.selection.availableGironi)
  const selectedGirone = useSelector(state => state.selection.selectedGirone)
  const availableTeams = useSelector(state => state.selection.availableTeams)
  const selectedTeam = useSelector(state => state.selection.selectedTeam)
  const availableNumeriGara = useSelector(
    state => state.selection.availableNumeriGara,
  )
  const selectedNumeroGara = useSelector(
    state => state.selection.selectedNumeroGara,
  )
  const availableProvvedimenti = useSelector(
    state => state.selection.availableProvvedimenti,
  )
  const selectedProvvedimento = useSelector(
    state => state.selection.selectedProvvedimento,
  )
  const availableArbitraggi = useSelector(
    state => state.selection.availableArbitraggi,
  )
  const selectedArbitraggio = useSelector(
    state => state.selection.selectedArbitraggio,
  )
  const availablePeriodi = useSelector(
    state => state.selection.availablePeriodi,
  )
  const selectedPeriodo = useSelector(state => state.selection.selectedPeriodo)
  const availableRitardi = useSelector(
    state => state.selection.availableRitardi,
  )
  const selectedRitardo = useSelector(state => state.selection.selectedRitardo)
  const availableTessere = useSelector(
    state => state.selection.availableTessere,
  )
  const selectedTessera = useSelector(state => state.selection.selectedTessera)
  const { data: availableArbitri } = useGetArbitriQuery(undefined, {
    skip: !yArbitro,
  })
  const selectedArbitro = useSelector(state => state.selection.selectedArbitro)
  const { data: availableSocieta } = useGetAffiliazioniLightQuery(undefined, {
    skip: !ySocieta,
  })
  const selectedSocieta = useSelector(state => state.selection.selectedSocieta)

  const {
    resetSelectedSocieta,
    resetSelectedArbitro,
    resetSelectedTessera,
    resetSelectedPeriodo,
    resetSelectedRitardo,
    resetSelectedArbitraggio,
    resetSelectedProvvedimento,
    resetSelectedNumeroGara,
    resetSelectedTeam,
    resetSelectedGirone,
    resetSelectedCategoria,
    resetSelectedCampionato,
    resetSelectedStagione,
  } = useSelectionReset()

  const dispatch = useDispatch()

  // check selectedStagione
  useEffect(() => {
    if (!selectedStagione && availableStagioni?.length > 0)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedStagione'))
        if (availableStagioni.includes(storedValue)) {
          dispatch(setSelectedStagione(storedValue))
        } else {
          dispatch(setSelectedStagione(availableStagioni[0]))
        }
      } catch {
        resetSelectedStagione()
      }

    if (
      selectedStagione &&
      availableStagioni?.length > 0 &&
      !availableStagioni?.includes(selectedStagione)
    )
      resetSelectedStagione()
  }, [availableStagioni, dispatch, resetSelectedStagione, selectedStagione])

  // check selectedCampionato
  useEffect(() => {
    if (!selectedCampionato && availableCampionati?.length > 0)
      try {
        const storedValue = JSON.parse(
          localStorage.getItem('selectedCampionato'),
        )
        dispatch(
          setSelectedCampionato({
            selectedCampionato: availableCampionati.includes(storedValue)
              ? storedValue
              : availableCampionati[0],
            campionati,
          }),
        )
      } catch {
        resetSelectedCampionato()
      }

    if (
      selectedCampionato &&
      availableCampionati?.length > 0 &&
      !availableCampionati?.includes(selectedCampionato)
    )
      resetSelectedCampionato()
  }, [
    availableCampionati,
    campionati,
    dispatch,
    resetSelectedCampionato,
    selectedCampionato,
  ])

  // check selectedCategoria
  useEffect(() => {
    if (!selectedCategoria && availableCategorie?.length > 1)
      try {
        const storedValue = JSON.parse(
          localStorage.getItem('selectedCategoria'),
        )
        if (availableCategorie.includes(storedValue)) {
          dispatch(setSelectedCategoria(storedValue))
        } else {
          resetSelectedCategoria()
        }
      } catch {
        resetSelectedCategoria()
      }

    if (
      selectedCategoria &&
      availableCategorie?.length > 1 &&
      !availableCategorie?.includes(selectedCategoria)
    )
      resetSelectedCategoria()
  }, [availableCategorie, dispatch, resetSelectedCategoria, selectedCategoria])

  // check selectedGirone
  useEffect(() => {
    if (!selectedGirone && availableGironi?.length > 1)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedGirone'))
        if (availableGironi.includes(storedValue)) {
          dispatch(setSelectedGirone(storedValue))
        } else {
          resetSelectedGirone()
        }
      } catch {
        resetSelectedGirone()
      }

    if (
      selectedGirone &&
      availableGironi?.length > 1 &&
      !availableGironi?.includes(selectedGirone)
    ) {
      resetSelectedGirone()
      resetSelectedTeam()
    }
  }, [
    availableGironi,
    dispatch,
    resetSelectedGirone,
    resetSelectedTeam,
    selectedGirone,
  ])

  // check selectedTeam
  useEffect(() => {
    if (!selectedTeam && availableTeams?.length > 0) {
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedTeam'))
        if (availableTeams.map(t => t?.id).includes(storedValue)) {
          dispatch(setSelectedTeam(storedValue))
        } else {
          resetSelectedTeam()
        }
      } catch {
        resetSelectedTeam()
      }
    }

    if (
      selectedTeam &&
      availableTeams?.length > 0 &&
      !availableTeams?.map(t => t?.id).includes(selectedTeam)
    )
      resetSelectedTeam()
  }, [availableTeams, dispatch, resetSelectedTeam, selectedTeam])

  // check selectedNumeroGara
  useEffect(() => {
    if (!selectedNumeroGara && availableNumeriGara?.length > 0) {
      try {
        const storedValue = JSON.parse(
          localStorage.getItem('selectedNumeroGara'),
        )
        if (availableNumeriGara.map(t => t?.numeroGara).includes(storedValue)) {
          dispatch(setSelectedNumeroGara(storedValue))
        } else {
          resetSelectedNumeroGara()
        }
      } catch {
        resetSelectedNumeroGara()
      }
    }

    if (
      selectedNumeroGara &&
      availableNumeriGara?.length > 0 &&
      !availableNumeriGara?.map(t => t?.numeroGara).includes(selectedNumeroGara)
    )
      resetSelectedNumeroGara()
  }, [
    availableNumeriGara,
    dispatch,
    resetSelectedNumeroGara,
    selectedNumeroGara,
  ])

  // check selectedProvvedimento
  useEffect(() => {
    if (!selectedProvvedimento && availableProvvedimenti?.length > 0)
      try {
        const storedValue = JSON.parse(
          localStorage.getItem('selectedProvvedimento'),
        )
        if (availableProvvedimenti.includes(storedValue)) {
          dispatch(setSelectedProvvedimento(storedValue))
        } else {
          dispatch(setSelectedProvvedimento(availableProvvedimenti[0]))
        }
      } catch {
        resetSelectedProvvedimento()
      }

    if (
      selectedProvvedimento &&
      availableProvvedimenti?.length > 0 &&
      !availableProvvedimenti?.includes(selectedProvvedimento)
    )
      resetSelectedProvvedimento()
  }, [
    availableProvvedimenti,
    dispatch,
    resetSelectedProvvedimento,
    selectedProvvedimento,
  ])

  // check selectedArbitraggio
  useEffect(() => {
    if (!selectedArbitraggio && availableArbitraggi?.length > 0)
      try {
        const storedValue = JSON.parse(
          localStorage.getItem('selectedArbitraggio'),
        )
        if (availableArbitraggi.includes(storedValue)) {
          dispatch(setSelectedArbitraggio(storedValue))
        } else {
          dispatch(setSelectedArbitraggio(availableArbitraggi[0]))
        }
      } catch {
        resetSelectedArbitraggio()
      }

    if (
      selectedArbitraggio &&
      availableArbitraggi?.length > 0 &&
      !availableArbitraggi?.includes(selectedArbitraggio)
    )
      resetSelectedArbitraggio()
  }, [
    availableArbitraggi,
    dispatch,
    resetSelectedArbitraggio,
    selectedArbitraggio,
  ])

  // check selectedPeriodo
  useEffect(() => {
    if (!selectedPeriodo && availablePeriodi?.length > 1)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedPeriodo'))
        if (availablePeriodi.includes(storedValue)) {
          dispatch(setSelectedPeriodo(storedValue))
        } else {
          dispatch(setSelectedPeriodo(availablePeriodi[0]))
        }
      } catch {
        resetSelectedPeriodo()
      }

    if (
      selectedPeriodo &&
      availablePeriodi?.length > 0 &&
      !availablePeriodi?.includes(selectedPeriodo)
    )
      resetSelectedPeriodo()
  }, [availablePeriodi, dispatch, resetSelectedPeriodo, selectedPeriodo])

  // check selectedRitardo
  useEffect(() => {
    if (!selectedRitardo && availableRitardi?.length > 1)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedRitardo'))
        if (availableRitardi.includes(storedValue)) {
          dispatch(setSelectedRitardo(storedValue))
        } else {
          dispatch(setSelectedRitardo(availableRitardi[0]))
        }
      } catch {
        resetSelectedRitardo()
      }

    if (
      selectedRitardo &&
      availableRitardi?.length > 0 &&
      !availableRitardi?.includes(selectedRitardo)
    )
      resetSelectedRitardo()
  }, [availableRitardi, dispatch, resetSelectedRitardo, selectedRitardo])

  // check selectedTessera
  useEffect(() => {
    if (!selectedTessera && availableTessere?.length > 1)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedTessera'))
        if (availableTessere.includes(storedValue)) {
          dispatch(setSelectedTessera(storedValue))
        } else {
          resetSelectedTessera()
        }
      } catch {
        resetSelectedTessera()
      }

    if (
      selectedTessera &&
      availableTessere?.length > 0 &&
      !availableTessere?.includes(selectedTessera)
    )
      resetSelectedTessera()
  }, [availableTessere, dispatch, resetSelectedTessera, selectedTessera])

  // check selectedArbitro
  useEffect(() => {
    if (!selectedArbitro && availableArbitri?.length > 0)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedArbitro'))
        if (availableArbitri?.map(a => a?.id).includes(storedValue)) {
          dispatch(setSelectedArbitro(storedValue))
        } else {
          resetSelectedArbitro()
        }
      } catch {
        resetSelectedArbitro()
      }

    if (
      selectedArbitro &&
      availableArbitri?.length > 0 &&
      !availableArbitri?.map(a => a?.id)?.includes(selectedArbitro)
    )
      resetSelectedArbitro()
  }, [availableArbitri, dispatch, resetSelectedArbitro, selectedArbitro])

  // check selectedSocieta
  useEffect(() => {
    if (!selectedSocieta && availableSocieta?.length > 0)
      try {
        const storedValue = JSON.parse(localStorage.getItem('selectedSocieta'))
        if (availableSocieta?.map(a => a?.id).includes(storedValue)) {
          dispatch(setSelectedSocieta(storedValue))
        } else {
          resetSelectedSocieta()
        }
      } catch {
        resetSelectedSocieta()
      }

    if (
      selectedSocieta &&
      availableSocieta?.length > 0 &&
      !availableSocieta?.map(a => a?.id)?.includes(selectedSocieta)
    )
      resetSelectedSocieta()
  }, [availableSocieta, dispatch, resetSelectedSocieta, selectedSocieta])
}
